import { useDeferredValue, useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useAppSelector } from "redux/hooks";
import { type ProductQueryV4Row, useGetV4ProductReportByProduct } from "../api/product/v4ProductReportByProduct";
import { css } from "../../../../styled-system/css";
import { type ColumnDef, getCoreRowModel, getExpandedRowModel, useReactTable } from "@tanstack/react-table";
import { type ManagedTableState, type ManagedTableStateSetters } from "components/table/usePersistedTableState";
import { HStack, VStack, Wrap } from "../../../../styled-system/jsx";
import { RT8ColumnSelector } from "components/core/blocks/MetricSelector";
import { SearchInput } from "components/fields/base/TextField";
import { RT8Pagination } from "components/table/Pagination";
import { Table } from "components/table/Table";
import { resetTable, selectionStateToSelectedIds } from "components/table/utils";
import { RT8FilterWidget } from "views/merchandise/tables/FilterWidget";
import { Button } from "components/core/basic/Button";
import { MdRotateLeft } from "react-icons/md";
import { useCategoriesQuery } from "hooks/useCategories";
import { useGetV4ProductReportOptions } from "../api/product/v4ProductReportOptions";
import { INVENTORY_STATE_OPTIONS, SALE_STATE_OPTIONS } from "components/custom/merchandise/constants";
import { DownloadV4Report } from "../DownloadReport";
import { useV4ProductReportExport } from "../api/product/v4ProductReportExport";
import { ENROLL_OPTIONS } from "views/advertising/productLifecycleFilters";
import { toTitleCase } from "utils/formatText";
import ProductCard from "components/custom/merchandise/ProductCard";
import { BulkActionButton } from "components/table/BulkActionButton";
import BulkTagsForm from "components/core/blocks/BulkTagsForm";
import { useBulkUpdateAllProductAdStatuses } from "modules/advertising/api/useBulkUpdateAllProductAdStatuses";
import { useBulkUpdateProductProperties } from "modules/advertising/api/useBulkUpdateProductProperties";

const ProductReportTable = ({
  columns,
  state,
  initialState,
  setters,
}: {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  columns: ColumnDef<ProductQueryV4Row, any>[];
  state: ManagedTableState;
  initialState: ManagedTableState;
  setters: ManagedTableStateSetters;
}) => {
  const [filledTags, setFilledTags] = useState<string[]>([]);

  const user = useAppSelector((state) => state.user);
  const marketPlace = useAppSelector((state) => state.marketPlace);
  const channel = useAppSelector((state) => state.channel.currentChannel);

  const deferredSearch = useDeferredValue(state.globalFilter);
  const queryClient = useQueryClient();

  const byProductOptionsGetter = useGetV4ProductReportByProduct();
  const productByProductReportQuery = useQuery(
    byProductOptionsGetter({
      pagination: state.pagination,
      sorting: state.sorting,
      filters: state.columnFilters,
      search: deferredSearch,
    }),
  );

  void queryClient.prefetchQuery({
    ...byProductOptionsGetter({
      pagination: { pageIndex: state.pagination.pageIndex + 1, pageSize: state.pagination.pageSize },
      sorting: state.sorting,
      filters: state.columnFilters,
      search: deferredSearch,
    }),
    staleTime: 10000,
  });

  const categoriesQuery = useCategoriesQuery();
  const exportMutation = useV4ProductReportExport(state.columnFilters);
  const productReportOptionsQuery = useQuery(useGetV4ProductReportOptions());

  const tagOptions = useMemo(
    () => [
      { value: "all", label: "All Tags" },
      ...(productReportOptionsQuery.data?.data.tags.map((tag) => {
        return {
          value: tag,
          label: tag,
        };
      }) ?? []),
    ],
    [productReportOptionsQuery.data?.data.tags],
  );

  const variantOptions = useMemo(() => {
    const { variations } = productReportOptionsQuery.data?.data ?? {};

    if (!variations) {
      return [];
    }

    return [
      { value: "all", label: "All Variants" },
      ...Object.keys(variations)
        .map((variationCategory) => {
          return variations[variationCategory]?.map((variation) => ({
            label: `${toTitleCase(variationCategory)}: ${variation}`,
            value: { [variationCategory]: variation },
          }));
        })
        .flat(),
    ];
  }, [productReportOptionsQuery.data?.data]);

  const categoryOptions = useMemo(
    () => [
      { value: "all", label: "All Categories" },
      ...(categoriesQuery.data?.map((category) => {
        return {
          value: category.id.toString(),
          label: category.leaf_category_name,
        };
      }) ?? []),
    ],
    [categoriesQuery.data],
  );
  const table = useReactTable({
    data: productByProductReportQuery.data?.data ?? [],
    columns,
    defaultColumn: {
      filterFn: () => true,
    },
    getCoreRowModel: getCoreRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    getRowCanExpand: () => true,
    getRowId: (row) => row.product_id.toString(),
    enableRowSelection: true,
    rowCount: productByProductReportQuery.data?.count || 0,
    initialState: initialState,
    state: state,
    ...setters,
    manualPagination: true,
    meta: {
      showSkeleton:
        productByProductReportQuery.isFetching &&
        (!productByProductReportQuery.data || productByProductReportQuery.isPlaceholderData),
      columnFilterSelectOptions: {
        category_id: categoryOptions,
        tags: tagOptions,
        variations: variantOptions.filter(Boolean),
        has_ads: [
          {
            value: true,
            label: "Active Ads",
          },
          {
            value: false,
            label: "No Active Ads",
          },
        ],
        inventory_stock_state: [
          {
            value: "all",
            label: "All Inventory States",
            description: "Show products with any inventory state",
          },
          {
            value: "IN_STOCK_SCARCE",
            label: "Low Stock",
            description: "Product stock is running low.",
          },
          ...Object.values(INVENTORY_STATE_OPTIONS)
            .filter((option) => option && option.value !== "LOW_STOCK")
            .filter(Boolean),
        ],
        pricing_plan_status: ENROLL_OPTIONS,
        sales_state: [
          {
            value: "all",
            label: "All Sales States",
          },
          ...SALE_STATE_OPTIONS.filter((option) => option !== undefined),
        ],
      },
    },
  });

  const bulkUpdateProductAdStatusMutation = useBulkUpdateAllProductAdStatuses();
  const bulkUpdateProductPropertiesMutation = useBulkUpdateProductProperties();

  const updateProductAdStatus = (newStatus: string) => {
    const productData = {
      product_ids: selected.map((id) => parseInt(id)),
      status: newStatus,
    };

    bulkUpdateProductAdStatusMutation.mutate(productData);
  };

  const selected = selectionStateToSelectedIds(table.getState().rowSelection);

  if (productByProductReportQuery.isError) {
    return (
      <div>
        Error loading product report. {import.meta.env.PROD ? null : JSON.stringify(productByProductReportQuery.error)}
      </div>
    );
  }

  return (
    <div>
      <Wrap justifyContent={"end"} width={"100%"} padding={"small"} fontSize={"large"}>
        <BulkActionButton
          selected={selected}
          options={[
            {
              label: "Add Product Tags",
              action: () => {
                bulkUpdateProductPropertiesMutation.mutate(
                  {
                    product_ids: selected.map((id) => parseInt(id)),
                    updates: [
                      {
                        field: "tags",
                        action: "add",
                        value: filledTags,
                      },
                    ],
                  },
                  { onSuccess: () => setFilledTags([]) },
                );
              },
              actionFormFields: (
                <BulkTagsForm
                  filledTags={filledTags}
                  setFilledTags={setFilledTags}
                  selected={selected}
                  suggestedTags={tagOptions.filter((t) => t.value !== "all").map((tag) => tag.value)}
                  taggedItemDescription={"product"}
                  actionType={"add"}
                />
              ),
              confirmationMessage: (
                <p>
                  Are you sure you want to add {filledTags.length > 1 ? "these tags" : "this tag"} to the selected
                  product
                  {selected.length > 1 ? "s" : ""}?
                </p>
              ),
            },
            {
              label: "Remove Product Tags",
              action: () => {
                bulkUpdateProductPropertiesMutation.mutate(
                  {
                    product_ids: selected.map((id) => parseInt(id)),
                    updates: [
                      {
                        field: "tags",
                        action: "remove",
                        value: filledTags,
                      },
                    ],
                  },
                  { onSuccess: () => setFilledTags([]) },
                );
              },
              actionFormFields: (
                <BulkTagsForm
                  filledTags={filledTags}
                  setFilledTags={setFilledTags}
                  selected={selected}
                  suggestedTags={tagOptions.filter((t) => t.value !== "all").map((tag) => tag.value)}
                  taggedItemDescription={"product"}
                  actionType={"remove"}
                />
              ),
              confirmationMessage: (
                <p>
                  Are you sure you want to remove {filledTags.length > 1 ? "these tags" : "this tag"} from the selected
                  product
                  {selected.length > 1 ? "s" : ""}?
                </p>
              ),
            },
            {
              label: "Pause Product Advertisements",
              action: () => {
                updateProductAdStatus("paused");
              },
              confirmationMessage: (
                <p>
                  Are you sure you want to pause all product advertisements for the selected product
                  {selected.length > 1 ? "s" : ""}?
                </p>
              ),
            },
            {
              label: "Enable Product Advertisements",
              action: () => {
                updateProductAdStatus("enabled");
              },
              confirmationMessage: (
                <p>
                  Are you sure you want to enable all product advertisements for the selected product
                  {selected.length > 1 ? "s" : ""}?
                </p>
              ),
            },
          ]}
        />
      </Wrap>
      <HStack padding={"small"} fontSize={"large"}>
        <VStack flex="1" alignItems={"start"}>
          <HStack width={"100%"}>
            <Button
              variant={"contentOnly"}
              onPress={() => {
                resetTable(table);
              }}
              fontSize={"xxlarge"}
            >
              <MdRotateLeft />
            </Button>
            <RT8ColumnSelector table={table} />
            <SearchInput
              aria-label={"Search"}
              isExpandable
              className={css({ flex: "0 1 350px" })}
              value={table.getState().globalFilter}
              onChange={table.setGlobalFilter}
            />
          </HStack>
          <RT8FilterWidget table={table} />
        </VStack>
        <RT8Pagination className={css({ marginLeft: "auto" })} table={table} />
      </HStack>
      <Table
        table={table}
        className={css({ minWidth: "100%" })}
        renderExpander={(row) => {
          return (
            <div
              className={css({
                width: "100%",
                padding: "large",
                backgroundColor: "#efefef",
              })}
            >
              <ProductCard
                productId={row.original.product_id}
                marketPlace={marketPlace}
                user={user}
                channel={channel}
                initialTab={"overview"}
                additionalTabs={[]}
              />
            </div>
          );
        }}
      />
      <HStack width={"100%"} padding={"small"}>
        <DownloadV4Report downloadMutation={exportMutation} />
      </HStack>
    </div>
  );
};

export default ProductReportTable;
